import { useEffect, useState } from 'react';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useInvitePracticeUser } from '../../../../hooks/usePracticeUsers';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { UDField } from '../../users/UDField';
import { UDInput } from '../../users/UDInput';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface NewUserPaneProps {
    practiceId: string;
    practiceName: string;
    onClose: () => void;
    /** Reports mutation state up so the shell can block its dismiss paths. */
    onBusyChange: (busy: boolean) => void;
}

/**
 * "New user" pane of AddPracticeUserModal - creates a brand new WordPress +
 * DB user via useInvitePracticeUser. Body and footer only; the modal's
 * overlay/card/header/toggle chrome lives in the parent.
 */
export function NewUserPane({ practiceId, practiceName, onClose, onBusyChange }: NewUserPaneProps) {
    const { showToast } = useToast();
    const invite = useInvitePracticeUser(practiceId);

    useEffect(() => {
        onBusyChange(invite.isPending);
        // Clear on unmount: the shell gates every dismiss path on this flag, so a
        // pane torn down mid-request would otherwise strand the modal with no way out.
        return () => onBusyChange(false);
    }, [invite.isPending, onBusyChange]);
    const [fullname, setFullname] = useState('');
    const [email, setEmail] = useState('');

    const canSubmit = fullname.trim().length > 0 && EMAIL_RE.test(email.trim());

    const handleSubmit = async () => {
        if (!canSubmit) return;
        try {
            await invite.mutateAsync({ fullname: fullname.trim(), email: email.trim() });
            showToast(`Invite sent to ${email.trim()}`, 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to invite user'), 'error');
        }
    };

    return (
        <>
            <div className="px-6 pb-5 flex flex-col gap-4">
                <p className="text-[13px] text-gray-600 leading-relaxed">
                    They&apos;ll receive an email to set their password.
                </p>
                <UDField label="Full name">
                    <UDInput
                        value={fullname}
                        onChange={setFullname}
                        placeholder="First and last name"
                        autoFocus
                    />
                </UDField>
                <UDField label="Email">
                    <UDInput
                        value={email}
                        onChange={setEmail}
                        placeholder={`name@${practiceName.toLowerCase().replace(/\s+/g, '')}.com`}
                    />
                </UDField>
                <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                    <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-[12px] text-gray-600">
                        They&apos;ll be linked to{' '}
                        <span className="font-semibold text-gray-900">{practiceName}</span>.
                    </span>
                </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2.5">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={invite.isPending}
                    className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={!canSubmit || invite.isPending}
                    onClick={handleSubmit}
                    className="bg-black text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {invite.isPending ? 'Sending…' : 'Send invite'}
                </button>
            </div>
        </>
    );
}
